import { useMutation, useQuery } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { Form, message } from 'antd'
import moment from 'moment'

import { DefectModal, Field, FormFieldValues } from './DefectModal'
import { GET_DEFECT_WITH_AUDIT_LOGS, UPDATE_DEFECT } from './graphQL'

import {
  convertToAssigneeInfo,
  convertToAssigneeInput,
} from '@/components/form-components/AssigneeSelect'
import { Button } from '@/components/shared-components/BUI/Button'
import {
  getProjectColour,
  ProjectIconWrapper,
  ProjectIcon,
} from '@/components/shared-components/ProjectIcon'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'

import {
  DefectUiQuery,
  DefectUiQueryVariables,
  UpdateDefectUiMutation,
  UpdateDefectUiMutationVariables,
} from 'types/api.graphql'

type Props = {
  id: string
  closeModal: () => void
  onUpdate?: (defect: UpdateDefectUiMutation['updateDefect']) => void
}

export const UpdateDefectModal: React.FC<Props> = ({
  id,
  closeModal,
  onUpdate,
}) => {
  const [form] = Form.useForm()

  const { data } = useQuery<DefectUiQuery, DefectUiQueryVariables>(
    GET_DEFECT_WITH_AUDIT_LOGS,
    { variables: { id } }
  )

  const [updateDefect, { loading: isSaving }] = useMutation<
    UpdateDefectUiMutation,
    UpdateDefectUiMutationVariables
  >(UPDATE_DEFECT, {
    awaitRefetchQueries: true,
    refetchQueries: [GET_DEFECT_WITH_AUDIT_LOGS],
  })

  const getInitialValues = (): FormFieldValues => {
    const defect = data?.defect
    if (!defect) return

    return {
      [Field.ID]: defect.id,
      [Field.CONTINUE]: false,
      [Field.TITLE]: defect.title,
      [Field.DESCRIPTION]: defect.description,
      [Field.TYPE]: defect.type,
      [Field.STATUS]: defect.status,
      [Field.PRIORITY]: defect.priority,
      [Field.PROJECT_ID]: defect.project.id,
      [Field.LOCATION]: defect.location?.id,
      [Field.DUE_DATE]: defect.dueDate ? moment(defect.dueDate) : null,
      [Field.ASSIGNEES]: convertToAssigneeInfo(defect.assignees),
      [Field.TRANSCRIPTIONS]: '',
      [Field.ATTACHMENTS]: defect.attachments.map(
        (a): FileUploadResult => ({
          name: a.fileName,
          attachmentId: a.id,
          uid: a.id,
          url: a.url,
        })
      ),
    }
  }

  const onSubmit = async (values: FormFieldValues) => {
    try {
      const updatedDefect = await updateDefect({
        variables: {
          id: data.defect.id,
          input: {
            title: values.title,
            description: values.description,
            type: values.type,
            priority: values.priority,
            status: values.status,
            assignees: convertToAssigneeInput(values.assignees),
            dueDate: values.dueDate
              ? moment(values.dueDate).toISOString()
              : null,
            locationId: values.location,
            attachmentIds: values.attachments
              ?.map((a) => a.attachmentId)
              ?.filter(Boolean),
          },
        },
      })

      onUpdate?.(updatedDefect.data.updateDefect)

      message.success('Defect successfully updated')
      closeModal()
    } catch (error) {
      // Swallow error so user can try resolve
      message.error('Error updating defect. You might not have permission.')

      Sentry.captureException(error)
    }
  }

  const isLoading = !data?.defect?.id

  return (
    <DefectModal
      form={form}
      open={!!id}
      contextualDate={data?.defect?.createdAt}
      closeModal={closeModal}
      onSubmit={onSubmit}
      title={
        !isLoading && (
          <ProjectIconWrapper>
            <ProjectIcon
              size={24}
              colour={getProjectColour(data.defect.project)}
            />
            {data.defect.project.name}
          </ProjectIconWrapper>
        )
      }
      footer={
        <Button
          variant="primary"
          type="submit"
          disabled={isLoading}
          loading={isSaving}
        >
          Save defect
        </Button>
      }
      isLoading={isLoading}
      initialValues={getInitialValues()}
    />
  )
}
